import React, { useCallback, useEffect, useState } from 'react';
import { meetingService } from '../../services/meetingService';
import { FirstStepSignupConfig } from '../../types/workshopFirstStepSignup';
import {
  assignmentAt,
  columnHeaders,
  firstStepSignupStatusLine,
  formatPresentationDate,
  myAssignment,
  totalSignupColumns,
  weekLockedForMember,
} from '../../utils/workshopFirstStepSignup';
import { formatNameFirstLastInitial } from '../../utils/userState';
import styles from '../../styles/workshopFirstStepSignup.module.css';

interface WorkshopFirstStepSignupProps {
  meetingCode: string;
  canFacilitate?: boolean;
  onConfigChange?: (config: FirstStepSignupConfig | null) => void;
  /** Parent fetched config (e.g. card defers load until expanded). */
  parentManagesLoad?: boolean;
  config?: FirstStepSignupConfig | null;
  loading?: boolean;
  viewerUserId?: string;
}

const WorkshopFirstStepSignup: React.FC<WorkshopFirstStepSignupProps> = ({
  meetingCode,
  canFacilitate = false,
  onConfigChange,
  parentManagesLoad = false,
  config: parentConfig = null,
  loading: parentLoading = false,
  viewerUserId,
}) => {
  const [config, setConfig] = useState<FirstStepSignupConfig | null>(parentManagesLoad ? parentConfig : null);
  const [loading, setLoading] = useState(!parentManagesLoad);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const applyConfig = useCallback(
    (next: FirstStepSignupConfig | null) => {
      setConfig(next);
      onConfigChange?.(next);
    },
    [onConfigChange]
  );

  const load = useCallback(async () => {
    setError('');
    try {
      const data = await meetingService.getFirstStepSignup(meetingCode);
      applyConfig(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      const notAvailable =
        msg.includes('not available') || msg.includes('HTTP 404') || msg === 'Not Found';
      applyConfig(null);
      setError(notAvailable ? '' : msg || 'Could not load sign-up sheet');
    } finally {
      setLoading(false);
    }
  }, [meetingCode, applyConfig]);

  useEffect(() => {
    if (parentManagesLoad) {
      setConfig(parentConfig);
      return;
    }
    void load();
  }, [load, parentManagesLoad, parentConfig]);

  const activeConfig = parentManagesLoad ? parentConfig : config;
  const activeLoading = parentManagesLoad ? parentLoading : loading;

  const facilitator = canFacilitate || activeConfig?.can_facilitate;

  const handleCell = async (weekIndex: number, columnIndex: number) => {
    if (!activeConfig || busy) return;
    const occupant = assignmentAt(activeConfig, weekIndex, columnIndex);
    const locked = weekLockedForMember(activeConfig, weekIndex);
    const mine = myAssignment(activeConfig, viewerUserId);
    const ownCell =
      !!mine && mine.week_index === weekIndex && mine.column_index === columnIndex;

    if (occupant) {
      if (ownCell) {
        if (locked) {
          setError('That week is locked.');
          setStatus('');
          return;
        }
        setBusy(true);
        setError('');
        try {
          const next = await meetingService.removeMyFirstStepSignup(meetingCode);
          applyConfig(next);
          setStatus('You removed your sign-up.');
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Could not remove sign-up');
        } finally {
          setBusy(false);
        }
        return;
      }

      if (facilitator) {
        setSelectedUserId(occupant.user_id);
        setStatus(
          `Selected ${formatNameFirstLastInitial(occupant.display_short || occupant.display_name, 'Member')}. Tap an empty slot.`
        );
        return;
      }

      return;
    }

    if (!facilitator && locked) {
      setError('That week is locked.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const next = await meetingService.assignFirstStepSignup(
        meetingCode,
        weekIndex,
        columnIndex,
        facilitator && selectedUserId ? selectedUserId : undefined
      );
      applyConfig(next);
      setSelectedUserId(null);
      setStatus('Sign-up updated.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update sign-up');
    } finally {
      setBusy(false);
    }
  };

  const toggleWeekLock = async (weekIndex: number, locked: boolean) => {
    if (!facilitator || busy) return;
    setBusy(true);
    setError('');
    try {
      const next = await meetingService.lockFirstStepSignupWeek(meetingCode, weekIndex, locked);
      applyConfig(next);
      setStatus(locked ? `Week ${weekIndex} locked.` : `Week ${weekIndex} unlocked.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update week lock');
    } finally {
      setBusy(false);
    }
  };

  if (activeLoading) {
    return <p className={styles.helper}>Loading presentation sign-up…</p>;
  }

  if (!activeConfig) {
    if (error) return <p className={styles.error}>{error}</p>;
    return (
      <p className={styles.helper}>
        First Step presentation sign-up is not open yet. Your facilitator will enable it when the
        workshop begins Step One.
      </p>
    );
  }

  if (!activeConfig.enabled && activeConfig.assignments.length === 0) {
    return (
      <p className={styles.helper}>
        First Step presentation sign-up is not open yet. Your facilitator will enable it when the
        workshop begins Step One.
      </p>
    );
  }

  const headers = columnHeaders(activeConfig);
  const columns = totalSignupColumns(activeConfig);
  const mine = myAssignment(activeConfig, viewerUserId);
  const weekCount = activeConfig.week_count;
  const readOnly = !activeConfig.enabled;

  const isOwnCell = (weekIndex: number, columnIndex: number) =>
    !!mine && mine.week_index === weekIndex && mine.column_index === columnIndex;

  const renderCell = (weekIndex: number, columnIndex: number) => {
    const occupant = assignmentAt(activeConfig, weekIndex, columnIndex);
    const locked = weekLockedForMember(activeConfig, weekIndex);
    const isAlt = columnIndex >= activeConfig.standard_slot_count;
    const ownCell = isOwnCell(weekIndex, columnIndex);
    const isSelected = !!(occupant && selectedUserId === occupant.user_id);

    let className = styles.cellEmpty;
    if (occupant) {
      className = ownCell ? styles.cellYou : isSelected ? styles.cellSelected : styles.cellTaken;
    } else if (!facilitator && locked) {
      className = styles.cellLocked;
    } else if (isAlt && !occupant) {
      className = styles.cellEmptyAlt;
    }

    let label: string;
    if (occupant) {
      const displayLabel = formatNameFirstLastInitial(
        occupant.display_short || occupant.display_name,
        'Member'
      );
      label = ownCell ? `${displayLabel} (you)` : displayLabel;
    } else if (locked && !facilitator) {
      label = '—';
    } else {
      label = '+ Sign up';
    }

    return (
      <button
        key={`c-${weekIndex}-${columnIndex}`}
        type="button"
        className={className}
        disabled={busy || readOnly || (!facilitator && locked && !ownCell)}
        onClick={() => void handleCell(weekIndex, columnIndex)}
      >
        {label}
      </button>
    );
  };

  const weekBlock = (weekIndex: number) => {
    const weekMeta = activeConfig.weeks?.find((w) => w.week_index === weekIndex);
    const dateLabel = formatPresentationDate(weekMeta?.presentation_date);
    const manuallyLocked = activeConfig.locked_week_indexes.includes(weekIndex);

    return (
      <React.Fragment key={`week-${weekIndex}`}>
        <div>
          <div className={styles.weekLabel}>Week {weekIndex}</div>
          {dateLabel && <div className={styles.weekDate}>{dateLabel}</div>}
          {facilitator && activeConfig.enabled && (
            <button
              type="button"
              className={styles.lockBtn}
              disabled={busy}
              onClick={() => void toggleWeekLock(weekIndex, !manuallyLocked)}
            >
              {manuallyLocked ? 'Unlock week' : 'Lock week'}
            </button>
          )}
        </div>
        {Array.from({ length: columns }, (_, col) => renderCell(weekIndex, col))}
      </React.Fragment>
    );
  };

  const signupStatusLine = firstStepSignupStatusLine(activeConfig, viewerUserId);

  return (
    <div className={styles.workshopFirstStepSignup}>
      <p className={styles.helper}>
        {readOnly
          ? 'This schedule is archived from when Step One sign-up was open.'
          : 'Sign up for one presentation slot. Tap an open cell to sign up or move. Tap your name to remove your sign-up.'}
        {facilitator && activeConfig.enabled && ' As facilitator, select a name then tap an empty cell to move them.'}
      </p>
      {signupStatusLine && <p className={styles.status}>{signupStatusLine}</p>}
      {status && <p className={styles.status}>{status}</p>}
      {error && <p className={styles.error}>{error}</p>}

      <div className={`${styles.gridWrap} ${styles.desktopOnly}`}>
        <div
          className={styles.grid}
          style={{ gridTemplateColumns: `7.5rem repeat(${columns}, minmax(6.5rem, 1fr))` }}
        >
          <div className={styles.gridHeader}>Week</div>
          {headers.map((h, i) => (
            <div
              key={`header-${i}`}
              className={i >= activeConfig.standard_slot_count ? styles.gridHeaderAlt : styles.gridHeader}
            >
              {h}
            </div>
          ))}
          {Array.from({ length: weekCount }, (_, i) => weekBlock(i + 1))}
        </div>
      </div>

      <div className={styles.mobileOnly}>
        {Array.from({ length: weekCount }, (_, i) => {
          const weekIndex = i + 1;
          const weekMeta = activeConfig.weeks?.find((w) => w.week_index === weekIndex);
          const dateLabel = formatPresentationDate(weekMeta?.presentation_date);
          return (
            <div key={`compact-${weekIndex}`} className={styles.compactWeek}>
              <div className={styles.compactWeekHeader}>
                <div className={styles.weekLabel}>Week {weekIndex}</div>
                {dateLabel && <div className={styles.weekDate}>{dateLabel}</div>}
              </div>
              {headers.map((h, col) => (
                <div key={`${weekIndex}-col-${col}`} className={styles.compactRow}>
                  <span className={styles.compactSlotLabel}>{h}</span>
                  {renderCell(weekIndex, col)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkshopFirstStepSignup;
